/**
 * Step 1: Select Template
 *
 * Optional step — user can pick a saved template to accelerate mapping,
 * download a blank template, or skip to upload.
 */

import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import { UploadSchema, UploadTemplate } from '../utils/types';

interface StepSelectTemplateProps {
	templates: UploadTemplate[];
	selectedTemplate: UploadTemplate | null;
	isLoading: boolean;
	onSelect: (template: UploadTemplate | null) => void;
	onDownloadTemplate: () => Promise<void>;
	schema: UploadSchema;
}

export const StepSelectTemplate: React.FC<StepSelectTemplateProps> = ({
	templates,
	selectedTemplate,
	isLoading,
	onSelect,
	onDownloadTemplate,
	schema,
}) => {
	const { t } = useTranslation();

	if (isLoading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box>
			<Typography variant="body1" sx={{ mb: 2 }}>
				{t('bulkUpload.selectTemplate.description')}
			</Typography>

			{/* Download template button */}
			<Box sx={{ mb: 3 }}>
				<Button
					variant="outlined"
					startIcon={<DownloadIcon />}
					onClick={() => onDownloadTemplate()}>
					{t('bulkUpload.selectTemplate.downloadTemplate', { label: schema.label })}
				</Button>
			</Box>

			<Divider sx={{ my: 2 }} />

			{/* Saved templates */}
			{templates.length > 0 ? (
				<>
					<Typography variant="subtitle2" sx={{ mb: 1 }}>
						{t('bulkUpload.selectTemplate.savedTemplates')}
					</Typography>
					<List dense>
						{templates.map((tmpl) => (
							<ListItemButton
								key={tmpl.id}
								selected={selectedTemplate?.id === tmpl.id}
								onClick={() => onSelect(selectedTemplate?.id === tmpl.id ? null : tmpl)}>
								<ListItemIcon>
									{selectedTemplate?.id === tmpl.id ? (
										<CheckCircleIcon color="primary" />
									) : (
										<DescriptionIcon />
									)}
								</ListItemIcon>
								<ListItemText
									primary={tmpl.name}
									secondary={
										tmpl.description ||
										t('bulkUpload.selectTemplate.mappedColumns', {
											count: tmpl.columnMappings.length,
										})
									}
								/>
							</ListItemButton>
						))}
					</List>
				</>
			) : (
				<Typography variant="body2" color="text.secondary">
					{t('bulkUpload.selectTemplate.noTemplates')}
				</Typography>
			)}
		</Box>
	);
};
